import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Base_URL } from "../Client/apiURL";
import { useAuth } from "../authentication/Auth";
import { countryCodes } from "../components/SideBar/data";
import { resolveCountry } from "../components/SideBar/countryResolver";
import {
  trackPhonePromptExited,
  trackPhoneSubmitAttempted,
  trackPhoneSubmitFailed,
} from "../analytics/contactNumberTracking";

const digitsOnly = (value) => String(value || "").replace(/\D/g, "");

const CompleteProfile = () => {
  const { user, applyUserData, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const initialCountry = useMemo(() => {
    return resolveCountry({
      iso: user?.CountryISO || user?.countryIso,
      dialCode: user?.CountryCode,
      defaultCode: "PK",
    });
  }, [user?.CountryCode, user?.CountryISO, user?.countryIso]);
  const [country, setCountry] = useState(initialCountry);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [error, setError] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const completedRef = useRef(false);
  const existingName = user?.UserName || user?.Name || "";

  useEffect(() => {
    if (Number(user?.PortalID) !== 1) {
      navigate("/", { replace: true });
      return undefined;
    }

    fetch(`${Base_URL}/app/users/contact-prompt-shown`, {
      method: "POST",
      credentials: "include",
    }).catch(() => {});

    return () => {
      if (!completedRef.current) trackPhonePromptExited();
    };
  }, [navigate, user?.PortalID]);

  const returnTo = () => {
    const from = location.state?.from;
    if (!from || from.pathname === "/complete-profile") return "/";
    return `${from.pathname || "/"}${from.search || ""}${from.hash || ""}`;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    trackPhoneSubmitAttempted();

    if (!country || !phoneNumber.trim()) {
      setError("Please select your country and enter a complete phone number.");
      trackPhoneSubmitFailed("validation");
      return;
    }

    setIsSaving(true);
    try {
      const formData = new FormData();
      formData.append("name", existingName);
      formData.append("countrycode", country.dialCode);
      formData.append("countryiso", country.code);
      formData.append("phonenumber", phoneNumber.trim());
      formData.append("city", user?.City || "");
      formData.append("pre_img_url", user?.ImageUrl || "");

      const response = await fetch(`${Base_URL}/app/users/complete-contact`, {
        method: "POST",
        body: formData,
        credentials: "include",
      });
      const result = await response.json();

      if (!response.ok || result.responseCode !== 200) {
        setError(result.message || "Unable to save your phone number.");
        trackPhoneSubmitFailed("server_rejected");
        return;
      }

      completedRef.current = true;
      applyUserData({
        ...(result.response || {}),
        PhoneRequired: false,
      });
      navigate(returnTo(), { replace: true });
    } catch (_requestError) {
      setError("Unable to save your phone number. Please try again.");
      trackPhoneSubmitFailed("network");
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogout = async () => {
    setIsLoggingOut(true);
    completedRef.current = true;
    trackPhonePromptExited();
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <main
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
        background: "#f7f4ff",
      }}
    >
      <form
        onSubmit={handleSubmit}
        style={{
          width: "100%",
          maxWidth: "460px",
          padding: "28px",
          borderRadius: "12px",
          background: "#fff",
          boxShadow: "0 12px 36px rgba(74, 48, 130, 0.14)",
        }}
      >
        <h1 style={{ margin: "0 0 10px", color: "#2e2350", fontSize: "24px" }}>
          Add your phone number
        </h1>
        <p style={{ margin: "0 0 22px", color: "#666", lineHeight: 1.5 }}>
          We use your number for account support and important SwiftUni updates, including subscription and payment-related messages. Enter a complete number with its country code to continue.
        </p>

        <label style={{ display: "block", marginBottom: "16px", color: "#3d315e", fontWeight: 600 }}>
          Country code
          <select
            value={country?.code || ""}
            onChange={(event) => setCountry(countryCodes.find((item) => item.code === event.target.value))}
            style={{
              display: "block",
              width: "100%",
              marginTop: "8px",
              padding: "12px",
              border: "1px solid #d8d1ec",
              borderRadius: "6px",
              background: "#fff",
            }}
          >
            {countryCodes.map((item) => (
              <option key={item.code} value={item.code}>
                {item.label} ({item.dialCode})
              </option>
            ))}
          </select>
        </label>

        <label style={{ display: "block", marginBottom: "18px", color: "#3d315e", fontWeight: 600 }}>
          Phone number
          <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
            <span
              style={{
                minWidth: "72px",
                padding: "12px 10px",
                border: "1px solid #d8d1ec",
                borderRadius: "6px",
                color: "#666",
                textAlign: "center",
              }}
            >
              {country?.dialCode || "+"}
            </span>
            <input
              value={phoneNumber}
              onChange={(event) => setPhoneNumber(event.target.value)}
              placeholder="Complete number"
              autoComplete="tel-national"
              inputMode="tel"
              style={{
                flex: 1,
                minWidth: 0,
                padding: "12px",
                border: "1px solid #d8d1ec",
                borderRadius: "6px",
              }}
            />
          </div>
        </label>

        {error && <p style={{ color: "#b42318", margin: "0 0 16px" }}>{error}</p>}
        <button
          type="submit"
          disabled={isSaving || isLoggingOut}
          style={{
            width: "100%",
            padding: "13px 18px",
            border: 0,
            borderRadius: "6px",
            background: isSaving ? "#b8a8e5" : "#7950e8",
            color: "#fff",
            fontSize: "16px",
            fontWeight: 700,
            cursor: isSaving ? "wait" : "pointer",
          }}
        >
          {isSaving ? "Saving..." : "Continue to SwiftUni"}
        </button>
        <button
          type="button"
          disabled={isSaving || isLoggingOut}
          onClick={handleLogout}
          style={{
            width: "100%",
            marginTop: "10px",
            padding: "11px 18px",
            border: "1px solid #d8d1ec",
            borderRadius: "6px",
            background: "#fff",
            color: "#5f527d",
            fontSize: "15px",
            fontWeight: 700,
            cursor: isSaving || isLoggingOut ? "wait" : "pointer",
          }}
        >
          {isLoggingOut ? "Logging out..." : "Log out"}
        </button>
      </form>
    </main>
  );
};

export default CompleteProfile;
